/*
 * SundayBuilder × Choral Intelligence™ · Inline Slot Picker
 * ---------------------------------------------------------------------
 * Renders governed anthem recommendations for a specific service slot.
 * Offertory and Communion auto-expand so directors see live choral
 * recommendations without an extra click. Recommendations are grounded
 * in the Sunday's readings when the plan provides them.
 * =================================================================== */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight } from "lucide-react";
import HymnalAvailability from "../repertoire/HymnalAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOMENT_LABEL = {
  offertory: "offertory",
  communion: "communion",
  entrance: "entrance",
  sending: "recessional",
  meditation: "meditation",
  preparation_of_the_gifts: "preparation",
  post_communion: "post-communion",
};

// Moments where the choir will almost always sing — surface recommendations
// by default so directors don't have to hunt for them.
const AUTO_EXPAND = new Set(["offertory", "communion"]);

export default function SlotAnthemPicker({
  date,
  liturgical_moment,
  serviceName,
  readings,
  limit = 3,
}) {
  const autoExpand = AUTO_EXPAND.has(liturgical_moment);
  const [expanded, setExpanded] = useState(autoExpand);
  const [state, setState] = useState({ loading: false, error: null, data: null });

  const fetchRecs = useCallback(() => {
    if (!date || !liturgical_moment) return;
    setState({ loading: true, error: null, data: null });
    const body = { date, liturgical_moment, limit };
    if (Array.isArray(readings) && readings.length > 0) {
      body.readings = readings;
    }
    axios
      .post(`${API}/sundaybuilder/choral-recommendations`, body)
      .then((r) => setState({ loading: false, error: null, data: r.data }))
      .catch((e) =>
        setState({
          loading: false,
          error: e.response?.data?.detail || "Choral recommendations are temporarily unavailable.",
          data: null,
        }),
      );
  }, [date, liturgical_moment, limit, readings]);

  // Auto-load on mount for offertory/communion so the recommendations
  // are visible without user action. Also refresh when readings change.
  useEffect(() => {
    if (autoExpand && !state.data && !state.loading) fetchRecs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, liturgical_moment, JSON.stringify(readings || [])]);

  const toggle = useCallback(() => {
    if (!expanded && !state.data && !state.loading) fetchRecs();
    setExpanded((v) => !v);
  }, [expanded, state.data, state.loading, fetchRecs]);

  const recs = state.data?.recommendations ?? [];
  const label = MOMENT_LABEL[liturgical_moment] || liturgical_moment;

  return (
    <div
      className="sb-slot-picker"
      data-testid={`sb-slot-picker-${liturgical_moment}`}
    >
      <button
        type="button"
        className="sb-slot-picker__trigger"
        aria-expanded={expanded}
        onClick={toggle}
        data-testid={`sb-slot-picker-trigger-${liturgical_moment}`}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Suggest anthem for {label}</span>
      </button>

      {expanded && (
        <div
          className="sb-slot-picker__body"
          data-testid={`sb-slot-picker-body-${liturgical_moment}`}
        >
          {state.loading && (
            <p className="sb-slot-picker__note" data-testid={`sb-slot-picker-loading-${liturgical_moment}`}>
              Consulting the governed catalog…
            </p>
          )}
          {state.error && (
            <p
              className="sb-slot-picker__note sb-slot-picker__note--error"
              data-testid={`sb-slot-picker-error-${liturgical_moment}`}
            >
              {state.error}
            </p>
          )}
          {!state.loading && !state.error && recs.length === 0 && state.data && (
            <p
              className="sb-slot-picker__note"
              data-testid={`sb-slot-picker-empty-${liturgical_moment}`}
            >
              No governed choral works fit this moment yet.
            </p>
          )}
          {!state.loading && !state.error && recs.length > 0 && (
            <ul
              className="sb-slot-picker__list"
              data-testid={`sb-slot-picker-list-${liturgical_moment}`}
            >
              {recs.map((rec) => (
                <li
                  key={rec.work.id}
                  className="sb-slot-picker__item"
                  data-testid={`sb-slot-picker-item-${liturgical_moment}-${rec.work.id}`}
                >
                  <div className="sb-slot-picker__item-head" style={{ cursor: "default" }}>
                    <span className="sb-slot-picker__item-title">{rec.work.title}</span>
                    <span className="sb-slot-picker__item-composer">
                      {rec.work.composer || "Anonymous"}
                    </span>
                  </div>
                  <HymnalAvailability availability={rec.availability || []} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
